// ---------------------------------------------------------------------------
// DesignSystemPage -- combines preview (flex-1) + customizer (280px)
// ---------------------------------------------------------------------------

import { useDesignSystem } from '../hooks/use-design-system'
import { DesignSystemPreview } from './design-system-preview'
import { DesignSystemControls } from './design-system-controls'
import { CogoMark } from '@/shell/ide-v2/components/cogo-mark'
import { useStudioLocale } from '@/i18n'
import type { UiBlockRef } from '../ui-blocks'

export interface DesignSystemPageProps {
  /**
   * Whether this component supplies its own title.
   *
   * It used to always print `<h1>Design System</h1>`, which is why two
   * different screens were both called "Design System": `/studio/design-system`
   * (whose own title is Design System) embeds this editor as a panel, and
   * `/studio/design-system-edit` renders it standalone. Embedded, the page
   * already names the panel, so a second title here would be both a duplicate
   * name and a second `<h1>` on the page.
   */
  showHeading?: boolean
  /** Read-only catalog block(s) to render with the current theme. */
  previewBlockRefs?: readonly UiBlockRef[]
}

export function DesignSystemPage({
  showHeading = true,
  previewBlockRefs,
}: DesignSystemPageProps = {}) {
  // Phase M1 Task 8: pass state directly — DesignSystemPreview's internal
  // buildPayload() derives both legacy themeTokens and resolved theme v2
  // from the same source so the two payload paths can never diverge.
  const { designSystem } = useDesignSystem()
  const { strings } = useStudioLocale()

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto bg-background p-6">
      {showHeading ? (
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <CogoMark size={14} />
              {strings.businessScreens.themeEditor.badge}
            </div>
            <h1 data-route-heading="design-system-edit" className="mt-3 text-2xl font-semibold tracking-tight text-foreground">{strings.designSystem.themeEditor}</h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">
              {strings.businessScreens.themeEditor.description}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground shadow-sm">
            {strings.businessScreens.themeEditor.livePreviewNote}
          </div>
        </header>
      ) : null}

      <div className="grid min-h-[640px] flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_288px]">
        <section className="min-h-[520px] overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <DesignSystemPreview
            state={designSystem}
            className="h-full"
            previewBlockRefs={previewBlockRefs}
          />
        </section>
        <DesignSystemControls />
      </div>
    </div>
  )
}
